import React, { useCallback, useLayoutEffect, useState } from "react";
import {
  PostDetailVideoFrame,
  PostDetailVideoPlay,
  PostDetailVideoPoster,
  PostDetailVideoSection,
} from "./PostDetail.styles";
import {
  SHARED_WISTIA_PLAYER_ID,
  bindPostDetailWistia,
  playPostDetailWistia,
  releasePostDetailWistia,
  setActivePostMediaId,
} from "./wistiaPostPlayer";

/** Prefer the post cover (sharp) over Wistia's often-blurry swatch. */
const getVideoPosterUrl = (post) =>
  post?.image?.original ||
  post?.croppedImageUrl ||
  post?.embeddedVideo?.thumbnailUrl ||
  null;

/**
 * Lovable video UX (cover + circular play) over prod Wistia playback.
 */
const PostDetailVideoPlayer = ({ post, isPublicAccessHidden = "" }) => {
  const mediaId = post?.embeddedVideo?.mediaId || "";
  const [started, setStarted] = useState(false);
  const posterUrl = getVideoPosterUrl(post);
  const title = post?.title || "Video";

  useLayoutEffect(() => {
    if (!mediaId) return undefined;
    setActivePostMediaId(mediaId);
    if (window.Wistia?.embeds?.setup) {
      window.Wistia.embeds.setup();
    }
    const timers = [300, 1000].map((ms) =>
      window.setTimeout(() => bindPostDetailWistia(mediaId), ms)
    );
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      setActivePostMediaId(null);
      releasePostDetailWistia();
    };
  }, [mediaId]);

  const handlePlay = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      setStarted(true);
      playPostDetailWistia(mediaId);
    },
    [mediaId]
  );

  if (!mediaId) return null;

  return (
    <PostDetailVideoSection className="post-detail-video">
      <PostDetailVideoFrame
        className={`post-detail-video__frame${started ? " is-playing" : ""}`}
      >
        <div
          key={mediaId}
          id="wistia_player"
          className={`wistia_embed wistia_async_${SHARED_WISTIA_PLAYER_ID} videoFoam=true roundedPlayer=0 playerBorderRadius=0 playButton=false post-detail-video__embed ${isPublicAccessHidden}`.trim()}
          aria-hidden={!started}
        />

        {!started && (
          <PostDetailVideoPoster
            type="button"
            className="post-detail-video__poster"
            onClick={handlePlay}
            aria-label={`Play ${title}`}
          >
            {posterUrl ? (
              <img src={posterUrl} alt="" loading="lazy" />
            ) : (
              <span className="post-detail-video__poster-fallback" aria-hidden="true" />
            )}
            <PostDetailVideoPlay aria-hidden="true">
              <span>
                <i className="material-icons">play_arrow</i>
              </span>
            </PostDetailVideoPlay>
          </PostDetailVideoPoster>
        )}
      </PostDetailVideoFrame>
    </PostDetailVideoSection>
  );
};

export default PostDetailVideoPlayer;
